/**
 * Lottie Logo Animation - Optimized for Performance
 *
 * Handles the animated logo using Lottie Web with performance optimizations
 */

import lottie from "lottie-web";

class LottieLogo {
	constructor() {
		this.container = null;
		this.animation = null;
		this.isLoaded = false;
		this.isLoading = false;
		this.retryCount = 0;
		this.maxRetries = 3;
		this.init();
	}

	init() {
		// Wait for DOM to be ready
		if (document.readyState === "loading") {
			document.addEventListener("DOMContentLoaded", () => this.setupLogo());
		} else {
			this.setupLogo();
		}
	}

	setupLogo() {
		this.container = document.querySelector(".logo__lottie");
		this.logoElement = document.querySelector(".logo");

		if (!this.container) {
			console.warn("Lottie logo container not found");
			return;
		}

		// Load animation immediately but with optimizations
		this.loadAnimation();
	}

	async loadAnimation() {
		if (this.isLoaded || this.isLoading) return;

		this.isLoading = true;

		try {
			// Get the JSON file path from data attribute
			const jsonPath =
				this.container.dataset.jsonPath ||
				"/resources/images/eurorope-logo.json";

			// Try to fetch the JSON first for better error handling
			const response = await fetch(jsonPath, {
				method: "GET",
				cache: "force-cache", // Use cached version if available
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const animationData = await response.json();

			// Initialize Lottie animation with the fetched data
			this.animation = lottie.loadAnimation({
				container: this.container,
				renderer: "svg",
				loop: false,
				autoplay: true,
				animationData: animationData, // Use animationData instead of path for better performance
				rendererSettings: {
					preserveAspectRatio: "xMidYMid meet",
					progressiveLoad: true, // Enable progressive loading
				},
			});

			// Handle animation events
			this.animation.addEventListener("DOMLoaded", () => {
				console.log("Lottie logo loaded successfully");
				this.isLoaded = true;
				this.isLoading = false;
				// Add loaded class to show the animation
				this.container.classList.add("loaded");
				this.hideFallback();
			});

			// Handle animation complete event voor vloeiende overgang
			this.animation.addEventListener("complete", () => {
				console.log("Lottie logo animation completed");
				// Dispatch custom event for other modules to listen to
				document.dispatchEvent(new CustomEvent('lottieLogoComplete'));
			});

			// Ook luisteren naar enterFrame voor vroegere trigger (optioneel)
			// this.animation.addEventListener("enterFrame", (event) => {
			// 	// Trigger H1 animatie net voor het einde voor nog vloeiender overgang
			// 	const progress = event.currentTime / event.totalTime;
			// 	if (progress >= 0.85 && !this.textAnimationTriggered) {
			// 		this.textAnimationTriggered = true;
			// 		document.dispatchEvent(new CustomEvent('lottieLogoNearComplete'));
			// 	}
			// });

			this.animation.addEventListener("error", (error) => {
				console.error("Lottie logo failed to load:", error);
				this.handleLoadError();
			});

			// Add hover interaction
			this.addHoverInteraction();
		} catch (error) {
			console.error("Failed to load Lottie animation:", error);
			this.handleLoadError();
		}
	}

	handleLoadError() {
		this.isLoading = false;
		this.retryCount++;

		if (this.retryCount < this.maxRetries) {
			// Retry after a short delay
			setTimeout(() => {
				console.log(
					`Retrying Lottie load (attempt ${this.retryCount + 1}/${this.maxRetries})`,
				);
				this.loadAnimation();
			}, 1000 * this.retryCount); // Exponential backoff
		} else {
			// Max retries reached, show fallback
			console.warn(
				"Max retries reached for Lottie animation, showing fallback",
			);
			this.showFallback();
		}
	}

	showFallback() {
		if (this.logoElement) {
			this.logoElement.classList.add("lottie-fallback");
		}
	}

	hideFallback() {
		if (this.logoElement) {
			this.logoElement.classList.remove("lottie-fallback");
		}
	}

	addHoverInteraction() {
		const logoLink = document.querySelector(".logo");

		if (!logoLink || !this.animation) return;

		logoLink.addEventListener("mouseenter", () => {
			// Play animation on hover (if you want this behavior)
			// this.animation.play();
		});

		logoLink.addEventListener("mouseleave", () => {
			// Stop or reset animation on hover out (if you want this behavior)
			// this.animation.stop();
		});
	}

	// Public method to control animation
	play() {
		if (this.animation && this.isLoaded) {
			this.animation.play();
		}
	}

	stop() {
		if (this.animation && this.isLoaded) {
			this.animation.stop();
		}
	}

	pause() {
		if (this.animation && this.isLoaded) {
			this.animation.pause();
		}
	}

	// Clean up method
	destroy() {
		if (this.animation) {
			this.animation.destroy();
			this.animation = null;
		}
		this.isLoaded = false;
		this.isLoading = false;
	}
}

// Initialize the Lottie logo
const lottieLogo = new LottieLogo();

// Export for potential external use
export default lottieLogo;
